import { useRef, useState } from 'react';
import useEditorUiStore from '../../stores/editorUiStore';

/**
 * 2026-09-14｜拖动轴锁按钮（对齐 Blender 轴锁的图标化入口）
 * - 单轴锁：X/Y/Z（拖动时仅该轴移动；再点取消）
 * - 平面锁：XY/XZ/YZ（拖动时仅在该平面内移动；对应快捷键 Shift+X/Y/Z）
 * - **可拖动**：长按「轴锁」标签或空白处拖拽移动（避免遮挡其它浮层；位置本机持久化）
 * 与快捷键（拖动中按 X/Y/Z、Shift+X/Y/Z）共用同一状态（editorUiStore.axisLock）。
 */
const POS_KEY = 'dtwin-axislock-pos';
function readPos(): { top: number; right: number } {
  try {
    const raw = localStorage.getItem(POS_KEY);
    const o = raw ? JSON.parse(raw) : null;
    if (o && typeof o.top === 'number' && typeof o.right === 'number') return o;
  } catch { /* ignore */ }
  return { top: 174, right: 16 };
}

export default function AxisLockButtons() {
  const axisLock = useEditorUiStore((s) => s.axisLock);
  const [pos, setPos] = useState(readPos);
  const posRef = useRef(pos);
  posRef.current = pos;

  const toggle = (k: string) => {
    const st = useEditorUiStore.getState();
    st.setAxisLock(st.axisLock === k ? null : k);
  };

  // 长按拖动（按钮区域不触发；「轴锁」标签与空白处为拖动把手）
  const onDragStart = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).tagName === 'BUTTON') return;
    e.preventDefault();
    const start = { sx: e.clientX, sy: e.clientY, top: pos.top, right: pos.right };
    const onMove = (ev: MouseEvent) => {
      setPos({
        top: Math.max(48, start.top + (ev.clientY - start.sy)),
        right: Math.max(8, start.right - (ev.clientX - start.sx)),
      });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      try {
        localStorage.setItem(POS_KEY, JSON.stringify(posRef.current));
      } catch { /* ignore */ }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const axisColors: Record<'x' | 'y' | 'z', string> = { x: '#ff4d4f', y: '#52c41a', z: '#1890ff' };
  const planeColors: Record<'xy' | 'xz' | 'yz', string> = { xy: '#faad14', xz: '#13c2c2', yz: '#eb2f96' };
  const btn = (k: string, color: string, label: string, title: string) => (
    <button
      key={k}
      onClick={() => toggle(k)}
      title={title}
      style={{
        minWidth: 22,
        height: 22,
        borderRadius: 4,
        border: `1px solid ${color}`,
        background: axisLock === k ? color : 'transparent',
        color: axisLock === k ? '#fff' : color,
        fontWeight: 700,
        fontSize: 10,
        lineHeight: '20px',
        padding: '0 3px',
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
  return (
    <div
      onMouseDown={onDragStart}
      style={{
        position: 'fixed',
        top: pos.top,
        right: pos.right,
        zIndex: 3400,
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        background: 'rgba(15,15,26,0.85)',
        border: '1px solid #30475e',
        borderRadius: 6,
        padding: '4px 6px',
        cursor: 'grab',
        userSelect: 'none',
      }}
      title="拖动轴锁：锁定后拖动仅沿该轴/平面移动（再点取消；快捷键：拖动中按 X/Y/Z 或 Shift+X/Y/Z）｜长按此处可拖动本工具条"
    >
      <span style={{ fontSize: 10, color: '#888', cursor: 'grab' }}>轴锁</span>
      {btn('x', axisColors.x, 'X', '锁定 X 轴（拖动时仅 X 移动；快捷键：拖动中按 X）')}
      {btn('y', axisColors.y, 'Y', '锁定 Y 轴（拖动时仅 Y 移动；快捷键：拖动中按 Y）')}
      {btn('z', axisColors.z, 'Z', '锁定 Z 轴（拖动时仅 Z 移动；快捷键：拖动中按 Z）')}
      <span style={{ fontSize: 10, color: '#555', margin: '0 2px' }}>|</span>
      <span style={{ fontSize: 10, color: '#888' }}>平面</span>
      {btn('xy', planeColors.xy, 'XY', '锁定 XY 平面（拖动时在 XY 平面内移动；快捷键：Shift+Z）')}
      {btn('xz', planeColors.xz, 'XZ', '锁定 XZ 平面（拖动时在 XZ 平面内移动；快捷键：Shift+Y）')}
      {btn('yz', planeColors.yz, 'YZ', '锁定 YZ 平面（拖动时在 YZ 平面内移动；快捷键：Shift+X）')}
    </div>
  );
}
